import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { RangeSlider } from '@/components/RangeSlider';
import { CalculatorNumberInput } from '@/components/inputs';
import { formatAmount } from '@/utils/formatting';
import { Language } from '@/constants/currencyConfig';
import { CalculatorType } from '@/sections/installments/Calculator';
const FIELD_MAX_PRINCIPAL = 'maxPrincipal';
const FIELD_PAYMENTS_COUNT = 'paymentsCount';
const FORM_NAME = 'installmentsCalculatorForm';
const STEP = 1000;
function getClosestAllowedValue(value: number, allowed: number[], min: number, max: number): number {
  if (allowed.length === 0) {
    return Math.min(Math.max(Math.ceil(value), min), max);
  }
  const clamped = Math.min(Math.max(Math.ceil(value), min), max);
  if (allowed.includes(clamped)) return clamped;
  return allowed.reduce((prev, curr) => Math.abs(curr - clamped) < Math.abs(prev - clamped) ? curr : prev);
}
type Props = {
  principalMax: number;
  principalMin: number;
  isLoading?: boolean;
  installments: {
    allowed: number[];
    min: number;
    max: number;
  };
  onChange: ({
    maxPrincipal,
    paymentsCount
  }: {
    maxPrincipal: number;
    paymentsCount: number;
  }) => void;
  type?: CalculatorType;
};
export function CalculatorForm(props) {
  const $ = _c(101);
  const {
    principalMax,
    principalMin,
    installments,
    onChange,
    isLoading
  } = props;
  const t0 = props.type === "loan" ? "loan" : "installments";
  let t1;
  if ($[0] !== t0) {
    t1 = [t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    t,
    i18n
  } = useTranslation(t1);
  const t2 = Math.ceil(principalMax / 2);
  let t3;
  if ($[2] !== t2) {
    t3 = {
      [FIELD_MAX_PRINCIPAL]: t2,
      [FIELD_PAYMENTS_COUNT]: 10
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [values, setValues] = useState(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      min: 3,
      max: 96,
      step: [3, 96]
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [installmentsRange, setInstallmentsRange] = useState(t4);
  let t5;
  if ($[5] !== onChange || $[6] !== values) {
    t5 = (fieldName, min, max, allowed) => input => {
      const value = typeof input === "number" ? input : typeof input === "string" ? parseFloat(input) : parseFloat(input.target.value);
      const fallbackValue = isNaN(value) ? min : value;
      const validatedValue = fieldName === FIELD_PAYMENTS_COUNT && allowed ? getClosestAllowedValue(fallbackValue, allowed, min, max) : Math.min(Math.max(Math.ceil(fallbackValue), min), max);
      const newValues = {
        ...values,
        [fieldName]: validatedValue
      };
      startTransition(() => {
        setValues(newValues);
      });
      onChange(newValues);
    };
    $[5] = onChange;
    $[6] = values;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleChange = t5;
  const handleChangeOnEnter = _temp;
  const handleCurrentPaymentChange = handleChange(FIELD_PAYMENTS_COUNT, installmentsRange.min, installmentsRange.max, installments.allowed);
  let t6;
  let t7;
  if ($[8] !== installments.allowed || $[9] !== installments.max || $[10] !== installments.min || $[11] !== isLoading || $[12] !== onChange || $[13] !== values) {
    t6 = () => {
      if (!isLoading && installments.allowed.length > 0) {
        startTransition(() => {
          setInstallmentsRange({
            min: installments.min,
            max: installments.max,
            step: installments.allowed
          });
        });
        const current = values[FIELD_PAYMENTS_COUNT];
        if (current < installments.min || current > installments.max) {
          const adjusted = Math.min(Math.max(current, installments.min), installments.max);
          if (adjusted !== current) {
            const newValues_0 = {
              ...values,
              [FIELD_PAYMENTS_COUNT]: adjusted
            };
            startTransition(() => {
              setValues(newValues_0);
            });
            onChange(newValues_0);
          }
        }
      }
    };
    t7 = [installments.min, installments.max, installments.allowed, isLoading, onChange, values];
    $[8] = installments.allowed;
    $[9] = installments.max;
    $[10] = installments.min;
    $[11] = isLoading;
    $[12] = onChange;
    $[13] = values;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let T0;
  let handleMaxPrincipalChange;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t8;
  let t9;
  if ($[16] !== handleChange || $[17] !== principalMax || $[18] !== principalMin || $[19] !== t || $[20] !== values) {
    handleMaxPrincipalChange = handleChange(FIELD_MAX_PRINCIPAL, principalMin, principalMax);
    t13 = FORM_NAME;
    t14 = "post";
    t12 = "flex flex-col gap-10";
    t10 = "mb-3 flex flex-row items-center justify-between";
    let t15;
    if ($[30] !== t) {
      t15 = t("calculator.price");
      $[30] = t;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    if ($[32] !== t15) {
      t11 = <p className="text-base font-semibold md:text-2xl">{t15}</p>;
      $[32] = t15;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    T0 = CalculatorNumberInput;
    t8 = values[FIELD_MAX_PRINCIPAL];
    t9 = handleChangeOnEnter(handleMaxPrincipalChange);
    $[16] = handleChange;
    $[17] = principalMax;
    $[18] = principalMin;
    $[19] = t;
    $[20] = values;
    $[21] = T0;
    $[22] = handleMaxPrincipalChange;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t8;
    $[29] = t9;
  } else {
    T0 = $[21];
    handleMaxPrincipalChange = $[22];
    t10 = $[23];
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t15;
  if ($[34] !== T0 || $[35] !== handleMaxPrincipalChange || $[36] !== principalMax || $[37] !== principalMin || $[38] !== t8 || $[39] !== t9) {
    t15 = <T0 value={t8} onKeyPress={t9} max={principalMax} min={principalMin} name={FIELD_MAX_PRINCIPAL} onBlur={handleMaxPrincipalChange} withCurrency={true} />;
    $[34] = T0;
    $[35] = handleMaxPrincipalChange;
    $[36] = principalMax;
    $[37] = principalMin;
    $[38] = t8;
    $[39] = t9;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t15) {
    t16 = <div className={t10}>{t11}{t15}</div>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const t17 = i18n.language as Language;
  let t18;
  if ($[45] !== principalMax || $[46] !== t17) {
    t18 = formatAmount(principalMax, t17);
    $[45] = principalMax;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const t19 = i18n.language as Language;
  let t20;
  if ($[48] !== principalMin || $[49] !== t19) {
    t20 = formatAmount(principalMin, t19);
    $[48] = principalMin;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const t21 = values[FIELD_MAX_PRINCIPAL];
  const t22 = values[FIELD_MAX_PRINCIPAL];
  let t23;
  if ($[51] !== handleMaxPrincipalChange || $[52] !== principalMax || $[53] !== principalMin || $[54] !== t18 || $[55] !== t20 || $[56] !== t21 || $[57] !== t22) {
    t23 = <RangeSlider max={principalMax} maxLabel={t18} minLabel={t20} min={principalMin} name={FIELD_MAX_PRINCIPAL} onBlur={handleMaxPrincipalChange} onChange={handleMaxPrincipalChange} step={STEP} value={t21} aria-valuemin={principalMin} aria-valuemax={principalMax} aria-valuenow={t22} />;
    $[51] = handleMaxPrincipalChange;
    $[52] = principalMax;
    $[53] = principalMin;
    $[54] = t18;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t16 || $[60] !== t23) {
    t24 = <div>{t16}{t23}</div>;
    $[59] = t16;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  const t25 = "mb-3 flex flex-row items-center justify-between";
  let t26;
  if ($[62] !== t) {
    t26 = t("calculator.numberOfInstallments");
    $[62] = t;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t26) {
    t27 = <p className="text-base font-semibold md:text-2xl">{t26}</p>;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  const T1 = CalculatorNumberInput;
  const t28 = installmentsRange.max;
  const t29 = installmentsRange.min;
  const t30 = FIELD_PAYMENTS_COUNT;
  const t31 = handleChangeOnEnter(handleCurrentPaymentChange);
  const t32 = values[FIELD_PAYMENTS_COUNT];
  let t33;
  if ($[66] !== T1 || $[67] !== handleCurrentPaymentChange || $[68] !== installmentsRange.max || $[69] !== installmentsRange.min || $[70] !== t30 || $[71] !== t31 || $[72] !== t32) {
    t33 = <T1 max={t28} min={t29} name={t30} onBlur={handleCurrentPaymentChange} onKeyPress={t31} value={t32} />;
    $[66] = T1;
    $[67] = handleCurrentPaymentChange;
    $[68] = installmentsRange.max;
    $[69] = installmentsRange.min;
    $[70] = t30;
    $[71] = t31;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t27 || $[75] !== t33) {
    t34 = <div className={t25}>{t27}{t33}</div>;
    $[74] = t27;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  const t35 = installmentsRange.max;
  const t36 = installmentsRange.min;
  let t37;
  if ($[77] !== installmentsRange.max) {
    t37 = installmentsRange.max.toString();
    $[77] = installmentsRange.max;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== installmentsRange.min) {
    t38 = installmentsRange.min.toString();
    $[79] = installmentsRange.min;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  const t39 = values[FIELD_PAYMENTS_COUNT];
  const t40 = values[FIELD_PAYMENTS_COUNT];
  let t41;
  if ($[81] !== handleCurrentPaymentChange || $[82] !== installmentsRange.max || $[83] !== installmentsRange.min || $[84] !== installmentsRange.step || $[85] !== t37 || $[86] !== t38 || $[87] !== t39 || $[88] !== t40) {
    t41 = <RangeSlider max={t35} min={t36} maxLabel={t37} minLabel={t38} name={FIELD_PAYMENTS_COUNT} onBlur={handleCurrentPaymentChange} onChange={handleCurrentPaymentChange} step={installmentsRange.step} value={t39} aria-valuemin={installmentsRange.min} aria-valuemax={installmentsRange.max} aria-valuenow={t40} />;
    $[81] = handleCurrentPaymentChange;
    $[82] = installmentsRange.max;
    $[83] = installmentsRange.min;
    $[84] = installmentsRange.step;
    $[85] = t37;
    $[86] = t38;
    $[87] = t39;
    $[88] = t40;
    $[89] = t41;
  } else {
    t41 = $[89];
  }
  let t42;
  if ($[90] !== t34 || $[91] !== t41) {
    t42 = <div>{t34}{t41}</div>;
    $[90] = t34;
    $[91] = t41;
    $[92] = t42;
  } else {
    t42 = $[92];
  }
  let t43;
  if ($[93] !== t12 || $[94] !== t24 || $[95] !== t42) {
    t43 = <div className={t12}>{t24}{t42}</div>;
    $[93] = t12;
    $[94] = t24;
    $[95] = t42;
    $[96] = t43;
  } else {
    t43 = $[96];
  }
  let t44;
  if ($[97] !== t13 || $[98] !== t14 || $[99] !== t43) {
    t44 = <form name={t13} method={t14}>{t43}</form>;
    $[97] = t13;
    $[98] = t14;
    $[99] = t43;
    $[100] = t44;
  } else {
    t44 = $[100];
  }
  return t44;
}
function _temp(changeHandler) {
  return e => {
    const keyCode = e.which || e.keyCode;
    if (keyCode === 13) {
      changeHandler(e.currentTarget.value.toString());
    }
  };
}